import { useCallback, useEffect, useState } from 'react';

/**
 * Zoom (1 decimal) y centro (6 decimales) del mapa, como strings para la UI.
 *
 * @param {import('maplibre-gl').Map} map
 * @returns {{ zoom: string, lat: string, lng: string }}
 */
export const readMapViewInfo = (map) => {
  const center = map.getCenter();
  return {
    zoom: map.getZoom().toFixed(1),
    lat: center.lat.toFixed(6),
    lng: center.lng.toFixed(6),
  };
};

const sameViewInfo = (a, b) => a.zoom === b.zoom && a.lat === b.lat && a.lng === b.lng;

/**
 * `mapInfo` de la página sin re-render por frame: durante el movimiento solo
 * cambia cuando cambia el zoom redondeado; el centro se publica en `moveend`.
 * Lo que necesita el centro en vivo (lectura de coordenadas) escucha `viewMap`.
 *
 * Llamar `syncMapViewInfo(map)` en el `onLoad` del mapa (también si se recrea).
 *
 * @param {{ zoom: string, lat: string, lng: string }} initialInfo
 * @returns {{
 *   mapInfo: { zoom: string, lat: string, lng: string },
 *   syncMapViewInfo: (map: import('maplibre-gl').Map) => void,
 *   viewMap: import('maplibre-gl').Map|null,
 * }}
 */
export function useMapViewInfo(initialInfo) {
  const [mapInfo, setMapInfo] = useState(initialInfo);
  const [viewMap, setViewMap] = useState(null);

  const syncMapViewInfo = useCallback((map) => {
    if (!map) return;
    setMapInfo(readMapViewInfo(map));
    setViewMap(map);
  }, []);

  useEffect(() => {
    if (!viewMap) return undefined;

    const handleMove = () => {
      const zoom = viewMap.getZoom().toFixed(1);
      setMapInfo((prev) => (prev.zoom === zoom ? prev : { ...prev, zoom }));
    };
    const handleMoveEnd = () => {
      const next = readMapViewInfo(viewMap);
      setMapInfo((prev) => (sameViewInfo(prev, next) ? prev : next));
    };

    viewMap.on('move', handleMove);
    viewMap.on('moveend', handleMoveEnd);
    return () => {
      try {
        viewMap.off('move', handleMove);
        viewMap.off('moveend', handleMoveEnd);
      } catch (_) { /* mapa ya removido */ }
    };
  }, [viewMap]);

  return { mapInfo, syncMapViewInfo, viewMap };
}
